import { create } from "zustand";
import {
  buildSeedSessionTree,
  groupOfDiscipline,
  mainIdForGroup,
  orderedEntries,
  type LibraryEntry,
  type LibrarySource,
  type MainSession,
  type SubSession,
} from "@/content/library";
import { useLibraryStore } from "@/store/libraryStore";

const STORAGE_KEY = "hippo.librarySessions";

interface Persisted {
  mainSessions: MainSession[];
  subSessions: SubSession[];
  /** IDs of built-in entries already merged, so deletions aren't resurrected. */
  seededIds: string[];
}

/**
 * Merge any built-in entries that shipped after the user's tree was first seeded.
 * New entries are appended to their group's main session (creating it if needed);
 * entries whose IDs are already in `seededIds` are left alone, so a built-in the
 * user deleted stays deleted. Persists once when it adds anything.
 */
function reconcileSeeds(p: Persisted): Persisted {
  const mains = [...p.mainSessions];
  const subs = [...p.subSessions];
  const present = new Set(subs.map((s) => s.id));
  // First upgrade (no seededIds yet): treat everything currently present as seeded.
  const seeded = new Set(p.seededIds.length ? p.seededIds : subs.map((s) => s.id));
  let added = false;

  for (const e of orderedEntries()) {
    if (seeded.has(e.id) || present.has(e.id)) {
      seeded.add(e.id);
      continue;
    }
    const group = groupOfDiscipline(e.disciplineIds[0]);
    const mainId = mainIdForGroup(group);
    if (!mains.some((m) => m.id === mainId)) {
      mains.push({ id: mainId, title: group, order: mains.length });
    }
    const order = subs.filter((s) => s.mainId === mainId).length;
    subs.push({
      id: e.id,
      mainId,
      title: e.title,
      body: e.body,
      disciplineId: e.disciplineIds[0],
      sources: e.sources,
      tags: e.tags,
      order,
    });
    present.add(e.id);
    seeded.add(e.id);
    added = true;
  }

  const next: Persisted = { mainSessions: mains, subSessions: subs, seededIds: [...seeded] };
  if (added || !p.seededIds.length) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }
  return next;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persisted>;
      if (Array.isArray(p.mainSessions) && Array.isArray(p.subSessions)) {
        return reconcileSeeds({
          mainSessions: p.mainSessions,
          subSessions: p.subSessions,
          seededIds: Array.isArray(p.seededIds) ? p.seededIds : [],
        });
      }
    }
  } catch {
    /* ignore */
  }
  // First run (or cleared storage): migrate the built-in library into sessions.
  const tree = buildSeedSessionTree();
  return { ...tree, seededIds: tree.subSessions.map((s) => s.id) };
}

export function newMainId(): string {
  return `main-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}
export function newSubId(): string {
  return `sub-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

function reorder(items: { order: number }[]): void {
  items
    .slice()
    .sort((a, b) => a.order - b.order)
    .forEach((it, i) => {
      it.order = i;
    });
}

interface State extends Persisted {
  /** Main sessions sorted by order. */
  orderedMains: () => MainSession[];
  /** Sub sessions for a main, sorted by order. */
  subsOf: (mainId: string) => SubSession[];
  subById: (id: string) => SubSession | undefined;
  /** Built-in entry shape for the Challenge "Study this" + drawer. */
  effectiveEntries: () => LibraryEntry[];
  entriesForDiscipline: (disciplineIds: string[]) => LibraryEntry[];

  addMain: (title: string) => string;
  renameMain: (id: string, title: string) => void;
  removeMain: (id: string) => void;
  moveMain: (id: string, dir: -1 | 1) => void;

  /** Create or update a sub-session. */
  saveSub: (sub: SubSession) => void;
  removeSub: (id: string) => void;
  moveSub: (id: string, dir: -1 | 1) => void;
}

function subToEntry(s: SubSession): LibraryEntry {
  return {
    id: s.id,
    disciplineIds: s.disciplineId ? [s.disciplineId] : [],
    title: s.title,
    summary: "",
    body: s.body,
    sources: s.sources ?? [],
    tags: s.tags ?? [],
  };
}

export const useLibraryContentStore = create<State>((set, get) => {
  const persist = () => {
    const { mainSessions, subSessions, seededIds } = get();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ mainSessions, subSessions, seededIds }));
  };

  return {
    ...load(),

    orderedMains: () => get().mainSessions.slice().sort((a, b) => a.order - b.order),
    subsOf: (mainId) =>
      get()
        .subSessions.filter((s) => s.mainId === mainId)
        .sort((a, b) => a.order - b.order),
    subById: (id) => get().subSessions.find((s) => s.id === id),
    effectiveEntries: () => get().subSessions.map(subToEntry),
    entriesForDiscipline: (disciplineIds) => {
      const set2 = new Set(disciplineIds);
      return get()
        .subSessions.filter((s) => s.disciplineId && set2.has(s.disciplineId))
        .map(subToEntry);
    },

    addMain: (title) => {
      const id = newMainId();
      const order = get().mainSessions.length;
      set({ mainSessions: [...get().mainSessions, { id, title: title.trim() || "New session", order }] });
      persist();
      return id;
    },
    renameMain: (id, title) => {
      set({ mainSessions: get().mainSessions.map((m) => (m.id === id ? { ...m, title: title.trim() || m.title } : m)) });
      persist();
    },
    removeMain: (id) => {
      // Remove the main and all of its sub-sessions, and their study data.
      const orphanIds = get().subSessions.filter((s) => s.mainId === id).map((s) => s.id);
      const mains = get().mainSessions.filter((m) => m.id !== id);
      reorder(mains);
      set({
        mainSessions: mains,
        subSessions: get().subSessions.filter((s) => s.mainId !== id),
      });
      useLibraryStore.getState().purge(orphanIds);
      persist();
    },
    moveMain: (id, dir) => {
      const mains = get().orderedMains();
      const i = mains.findIndex((m) => m.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= mains.length) return;
      [mains[i].order, mains[j].order] = [mains[j].order, mains[i].order];
      set({ mainSessions: [...mains] });
      persist();
    },

    saveSub: (sub) => {
      const exists = get().subSessions.some((s) => s.id === sub.id);
      if (exists) {
        set({ subSessions: get().subSessions.map((s) => (s.id === sub.id ? sub : s)) });
      } else {
        const order = get().subSessions.filter((s) => s.mainId === sub.mainId).length;
        set({ subSessions: [...get().subSessions, { ...sub, order }] });
      }
      persist();
    },
    removeSub: (id) => {
      const target = get().subById(id);
      const subs = get().subSessions.filter((s) => s.id !== id);
      if (target) reorder(subs.filter((s) => s.mainId === target.mainId));
      set({ subSessions: subs });
      useLibraryStore.getState().purge([id]);
      persist();
    },
    moveSub: (id, dir) => {
      const target = get().subById(id);
      if (!target) return;
      const siblings = get().subsOf(target.mainId);
      const i = siblings.findIndex((s) => s.id === id);
      const j = i + dir;
      if (j < 0 || j >= siblings.length) return;
      [siblings[i].order, siblings[j].order] = [siblings[j].order, siblings[i].order];
      set({ subSessions: [...get().subSessions] });
      persist();
    },
  };
});

export type { MainSession, SubSession, LibrarySource };
